import { useEffect, useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { toast } from 'react-toastify';
import {
  copyCouponIconSrc,
  redemptionCouponBoxSrc,
  redemptionSuccessCheckSrc,
} from '../../assets/bazzar';
import { extractRedemptionCoupon } from '../../services/bazaarService';
import { showAppError } from '../../services/messageDialogService';
import {
  BazaarActionButton,
  BazaarBackLink,
  BazaarDialogShell,
  BazaarRibbonTitle,
} from './bazaarDialogShared';

const SUMMARY_MIN_HEIGHT = 392;
const COUPON_MIN_HEIGHT = 451;
const SUCCESS_ICON_SIZE = 77;
const COUPON_BOX_WIDTH = 296;
const COUPON_BOX_HEIGHT = 78;
const CONTENT_WIDTH = 306;

/** Figma 10333:54447 */
const couponRedeemedTextSx = {
  fontFamily: 'Inter, sans-serif',
  color: '#fff',
  fontWeight: 600,
  fontSize: 16,
  lineHeight: '24px',
  letterSpacing: '0.8px',
  textAlign: 'center',
  width: '100%',
  maxWidth: CONTENT_WIDTH,
  mx: 'auto',
};

/** Figma 10333:54459 + 54457 + 54462 + 54445 + 54446 */
function RedemptionSuccessIcon() {
  return (
    <Box
      sx={{
        position: 'relative',
        width: 90,
        height: 82,
        mx: 'auto',
        mt: 2.5,
      }}
    >
      
      <Box
        component="img"
        src={redemptionSuccessCheckSrc}
        alt="Redemption successful"
        sx={{
          position: 'absolute',
          left: '50%',
          top: 0,
          transform: 'translateX(-50%)',
          width: SUCCESS_ICON_SIZE,
          height: SUCCESS_ICON_SIZE,
          display: 'block',
        }}
      />
    </Box>
  );
}

function formatCouponCode(code = '', couponType = '') {
  const trimmed = String(code).trim();
  if (!trimmed) return '';
  if (couponType === 'URL' || /^https?:\/\//i.test(trimmed)) return trimmed;
  return trimmed.startsWith('#') ? trimmed : `#${trimmed}`;
}

function getCouponType(redemption) {
  return String(redemption?.couponType || redemption?.raw?.couponType || '').trim().toUpperCase();
}

function getCouponMessage(card) {
  const brand = card?.brand || card?.title || 'Voucher';
  return {
    line1: `${brand} Coupon Redeemed`,
    line2: 'Use the coupon code and enjoy',
  };
}

export default function RedemptionSuccessDialog({
  open,
  card,
  redemption,
  onClose,
  initialStep = 'summary',
  backLabel = 'Back to RapBazaar',
}) {
  const [step, setStep] = useState(initialStep);
  const [copied, setCopied] = useState(false);
  const couponType = useMemo(() => getCouponType(redemption), [redemption]);
  const couponCode = useMemo(
    () => formatCouponCode(extractRedemptionCoupon(redemption), couponType),
    [redemption, couponType],
  );
  const isUrlCoupon = couponType === 'URL' || /^https?:\/\//i.test(couponCode);
  const { line1, line2 } = getCouponMessage(card);

  useEffect(() => {
    if (!open) {
      setStep(initialStep);
      setCopied(false);
      return;
    }

    setStep(initialStep);
    setCopied(false);
  }, [open, initialStep]);

  const handleCopyCoupon = async () => {
    if (!couponCode) {
      showAppError('Coupon code is not available.');
      return;
    }

    const plainCode = couponCode.replace(/^#/, '');

    try {
      await navigator.clipboard.writeText(plainCode);
      setCopied(true);
      toast.success('Coupon code copied!');
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      showAppError('Unable to copy coupon code.');
    }
  };

  const handleAccessCoupon = () => {
    if (!couponCode) {
      showAppError('Coupon code is not available.');
      return;
    }
    setStep('coupon');
  };

  const handleClose = () => {
    setStep(initialStep);
    onClose();
  };

  return (
    <BazaarDialogShell
      open={open}
      onClose={handleClose}
      minHeight={step === 'summary' ? SUMMARY_MIN_HEIGHT : COUPON_MIN_HEIGHT}
    >
      <BazaarRibbonTitle>Redemption Successful!</BazaarRibbonTitle>

      {step === 'summary' ? <RedemptionSuccessIcon /> : (
        <Box
          component="img"
          src={redemptionSuccessCheckSrc}
          alt="Redemption successful"
          sx={{
            width: SUCCESS_ICON_SIZE,
            height: SUCCESS_ICON_SIZE,
            display: 'block',
            mx: 'auto',
            mt: 2.5,
          }}
        />
      )}

      {step === 'summary' ? (
        <>
          <Typography sx={{ ...couponRedeemedTextSx, mt: 2.5 }}>
            Coupon Redeemed
          </Typography>

          <Box sx={{ mt: 3, px: 0.5 }}>
            <BazaarActionButton
              label="Access Coupon"
              onClick={handleAccessCoupon}
              disabled={redemption?.canAccessCoupon === false}
            />
          </Box>
        </>
      ) : (
        <>
          <Typography
            sx={{
              mt: 2.2,
              color: '#fff',
              fontWeight: 600,
              fontSize: 16,
              lineHeight: '24px',
              letterSpacing: '0.8px',
              textAlign: 'center',
              maxWidth: 306,
              mx: 'auto',
            }}
          >
            {line1}
            <br />
            {line2}
          </Typography>

          <Box
            component="button"
            type="button"
            onClick={handleCopyCoupon}
            disabled={!couponCode}
            aria-label={isUrlCoupon ? 'Copy coupon link' : 'Copy coupon code'}
            sx={{
              position: 'relative',
              display: 'block',
              width: '100%',
              maxWidth: COUPON_BOX_WIDTH,
              minWidth: 0,
              height: isUrlCoupon ? 96 : COUPON_BOX_HEIGHT,
              mx: 'auto',
              mt: 2.4,
              p: 0,
              border: 0,
              bgcolor: 'transparent',
              overflow: 'hidden',
              boxSizing: 'border-box',
              cursor: couponCode ? 'pointer' : 'not-allowed',
              opacity: couponCode ? 1 : 0.6,
            }}
          >
            <Box
              component="img"
              src={redemptionCouponBoxSrc}
              alt=""
              sx={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                maxWidth: '100%',
                display: 'block',
                objectFit: 'fill',
                pointerEvents: 'none',
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                left: 18,
                right: 18,
                top: 10,
                bottom: 10,
                minWidth: 0,
                px: 0.5,
                boxSizing: 'border-box',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'stretch',
                justifyContent: 'center',
                gap: 0.25,
                overflow: 'hidden',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, flexShrink: 0 }}>
                <Typography
                  sx={{
                    color: '#fff',
                    fontWeight: 400,
                    fontSize: 10,
                    lineHeight: '12px',
                    letterSpacing: 0,
                    opacity: 0.5,
                  }}
                >
                  {copied ? 'Copied!' : isUrlCoupon ? 'Copy Link' : 'Copy Coupon Code'}
                </Typography>
                <Box component="img" src={copyCouponIconSrc} alt="" sx={{ width: 16, height: 16, display: 'block' }} />
              </Box>
              <Typography
                sx={{
                  color: '#fff',
                  fontWeight: 700,
                  fontSize: isUrlCoupon ? 12 : 20,
                  lineHeight: isUrlCoupon ? '16px' : '32px',
                  letterSpacing: isUrlCoupon ? 0 : '0.8px',
                  textAlign: 'center',
                  minWidth: 0,
                  maxWidth: '100%',
                  overflowWrap: 'anywhere',
                  wordBreak: 'break-word',
                  whiteSpace: 'normal',
                }}
              >
                {couponCode || '—'}
              </Typography>
            </Box>
          </Box>
        </>
      )}

      <BazaarBackLink onClick={handleClose} label={backLabel} sx={step === 'summary' ? { mt: 3.5 } : undefined} />
    </BazaarDialogShell>
  );
}
